import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, Edit2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function VendorMasterPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [editVendor, setEditVendor] = useState(null);
  const [editName, setEditName] = useState("");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [rates, setRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [designs, setDesigns] = useState([]);
  const [rateDesignId, setRateDesignId] = useState("");
  const [rateProductCost, setRateProductCost] = useState("");
  const [designSearch, setDesignSearch] = useState("");

  const fetchVendors = useCallback(async () => {
    try {
      const res = await api.get("/vendors");
      setVendors(res.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  const fetchDesigns = useCallback(async () => {
    try {
      const res = await api.get("/saved-designs");
      setDesigns(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchVendors(); fetchDesigns(); }, [fetchVendors, fetchDesigns]);

  const fetchRates = useCallback(async (vid) => {
    setRatesLoading(true);
    try {
      const res = await api.get(`/vendor-rates/${vid}`);
      setRates(res.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRatesLoading(false); }
  }, []);

  const addVendor = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/vendors", { name: newName.trim() });
      toast.success("Vendor added");
      setNewName(""); setAddOpen(false); fetchVendors();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const updateVendor = async () => {
    if (!editName.trim() || !editVendor) return;
    try {
      await api.put(`/vendors/${editVendor.id}`, { name: editName.trim() });
      toast.success("Vendor updated"); setEditVendor(null); fetchVendors();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteVendor = async (id) => {
    try {
      await api.delete(`/vendors/${id}`);
      toast.success("Vendor deleted");
      if (selectedVendor?.id === id) { setSelectedVendor(null); setRates([]); }
      fetchVendors();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const selectVendor = (v) => {
    setSelectedVendor(v);
    fetchRates(v.id);
    setRateDesignId(""); setRateProductCost(""); setDesignSearch("");
  };

  const addRate = async () => {
    if (!rateDesignId || !rateProductCost) return;
    try {
      await api.post("/vendor-rates", {
        vendor_id: selectedVendor.id,
        design_id: rateDesignId,
        product_cost: parseFloat(rateProductCost),
      });
      toast.success("Rate saved");
      setRateDesignId(""); setRateProductCost(""); setDesignSearch("");
      fetchRates(selectedVendor.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteRate = async (id) => {
    try {
      await api.delete(`/vendor-rates/${id}`);
      toast.success("Rate deleted");
      fetchRates(selectedVendor.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const getDesignName = (did) => designs.find((d) => d.id === did)?.design_name || did;
  const filteredDesigns = designs.filter((d) => d.design_name.toLowerCase().includes(designSearch.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="vendor-master-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Vendor Master</h1>
        <p className="text-sm text-stone-500 mt-1">Manage vendors and their product cost rates for FOB designs.</p>
      </div>

      <div className="flex gap-6">
        {/* Left: Vendor list */}
        <div className="w-64 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Vendors</p>
            <Button size="sm" data-testid="add-vendor-button" className="h-7 bg-[#C2410C] hover:bg-[#9A3412] text-white text-xs" onClick={() => setAddOpen(true)}>
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            {loading ? (
              <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-stone-400" /></div>
            ) : vendors.length === 0 ? (
              <div className="p-6 text-center text-sm text-stone-400">No vendors yet</div>
            ) : (
              vendors.map((v) => (
                <div key={v.id} data-testid={`vendor-item-${v.id}`}
                  className={`flex items-center justify-between px-3 py-2.5 cursor-pointer border-b border-stone-100 last:border-0 transition-colors ${selectedVendor?.id === v.id ? "bg-orange-50 text-[#C2410C]" : "hover:bg-stone-50"}`}
                  onClick={() => selectVendor(v)}
                >
                  <span className="text-sm font-medium truncate flex-1">{v.name}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <button className="p-1 hover:text-blue-600" onClick={(e) => { e.stopPropagation(); setEditVendor(v); setEditName(v.name); }}><Edit2 className="h-3 w-3" /></button>
                    <button className="p-1 hover:text-red-600" onClick={(e) => { e.stopPropagation(); deleteVendor(v.id); }}><Trash2 className="h-3 w-3" /></button>
                    <ChevronRight className="h-3.5 w-3.5 text-stone-300" />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: Rates */}
        <div className="flex-1 min-w-0">
          {!selectedVendor ? (
            <div className="bg-white border border-stone-200 rounded-lg p-12 text-center text-stone-400 text-sm">Select a vendor to manage their product cost rates</div>
          ) : (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-stone-900">{selectedVendor.name}</h2>
                <p className="text-xs text-stone-500">Product cost per design for this vendor</p>
              </div>

              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Add / Update Rate</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-stone-600 mb-1 block">Design</Label>
                    <div className="relative">
                      <Input
                        data-testid="vendor-rate-design-search"
                        placeholder="Search designs..."
                        value={rateDesignId ? getDesignName(rateDesignId) : designSearch}
                        onChange={(e) => { setDesignSearch(e.target.value); setRateDesignId(""); }}
                        onFocus={() => setRateDesignId("")}
                        className="h-9"
                      />
                      {!rateDesignId && designSearch && (
                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
                          {filteredDesigns.slice(0, 30).map((d) => (
                            <div key={d.id} className="px-3 py-2 text-sm cursor-pointer hover:bg-orange-50 hover:text-[#C2410C]"
                              onClick={() => { setRateDesignId(d.id); setDesignSearch(""); }}>
                              {d.design_name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Product Cost</Label>
                    <div className="flex gap-2">
                      <Input data-testid="vendor-rate-cost" type="number" step="0.01" className="h-9" value={rateProductCost} onChange={(e) => setRateProductCost(e.target.value)} placeholder="Cost" />
                      <Button data-testid="add-vendor-rate-button" className="h-9 bg-[#C2410C] hover:bg-[#9A3412] text-white shrink-0" onClick={addRate}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-50">
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-2.5">Design</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-2.5 text-right">Product Cost</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-2.5 w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ratesLoading ? (
                      <TableRow><TableCell colSpan={3} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-stone-400" /></TableCell></TableRow>
                    ) : rates.length === 0 ? (
                      <TableRow><TableCell colSpan={3} className="text-center py-8 text-stone-400 text-sm">No rates added yet</TableCell></TableRow>
                    ) : (
                      rates.map((r) => (
                        <TableRow key={r.id} data-testid={`vendor-rate-row-${r.id}`}>
                          <TableCell className="px-4 py-2 text-sm">{getDesignName(r.design_id)}</TableCell>
                          <TableCell className="px-4 py-2 text-sm text-right font-mono">{r.product_cost}</TableCell>
                          <TableCell className="px-4 py-2">
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-stone-400 hover:text-red-600" onClick={() => deleteRate(r.id)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Vendor</DialogTitle><DialogDescription>Enter the vendor's name</DialogDescription></DialogHeader>
          <Input data-testid="vendor-name-input" placeholder="Vendor name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addVendor(); }} autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-add-vendor" className="bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={addVendor}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editVendor} onOpenChange={() => setEditVendor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit Vendor</DialogTitle></DialogHeader>
          <Input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") updateVendor(); }} autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditVendor(null)}>Cancel</Button>
            <Button className="bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={updateVendor}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
